import { getAtividadesCarro } from '../api/atividadeCarroApi';
import { AtividadeCarro, isServicoEmAberto } from '../interfaces/AtividadeCarro';
import { notify } from '../shared/feedback/notify';

/**
 * Serviços do veículo que ainda não foram concluídos (at-46, ADR 0021).
 *
 * Substitui as cinco cópias de `checkPendingConveyorBelts` que existiam espalhadas (tela do
 * Chefe, modal do atendimento, Controle de Produção, modal de checklist). Aquela versão
 * derivava "realizada" de **qualquer** período APPROVED em `CarStatusHistory` — o que passou a
 * mentir com o serviço inacabado: uma esteira por onde o veículo já passou aparecia como
 * concluída e o aviso antes da Entrega deixava de disparar justamente no caso em que ele
 * importa. Agora a fonte de verdade é o `estado` da atividade.
 */

export interface ServicosPendentes {
  /** Serviço começou e não terminou (estado PAUSADA ou EM_ANDAMENTO). */
  inacabados: string[];
  /** Etapa planejada que nunca foi trabalhada (estado PENDENTE). */
  naoRealizadas: string[];
  /**
   * at-60: `true` quando a consulta falhou (403, 500, rede) e **nada pôde ser verificado**.
   * Antes existiam só dois estados possíveis — "há pendências" e "não há pendências" — e a
   * falha caía no segundo: o util devolvia listas vazias, o aviso de serviço em aberto sumia
   * e o veículo podia ser entregue com serviço inacabado sem ninguém saber. Com listas vazias
   * **e** esta flag, quem consome sabe distinguir "verifiquei e está limpo" de "não consegui
   * verificar". Os arrays continuam vazios de propósito: nenhum nome de esteira é conhecido.
   */
  verificacaoIndisponivel: boolean;
}

const nomeDaEsteira = (a: AtividadeCarro): string =>
  a.esteira?.nome || a.statusLabel || a.esteiraId || 'esteira sem nome';

/**
 * Consulta os serviços em aberto do veículo. `ignorarEsteiraIds` (opcional) exclui esteiras da
 * checagem — ao mandar o veículo para a Entrega, a própria Entrega não é pendência.
 *
 * **Nunca rejeita.** A falha vira `verificacaoIndisponivel: true` (+ toast de aviso) em vez de
 * exceção porque há chamadores que a invocam sem `try/catch`: uma promise rejeitada abortaria a
 * ação (aprovar/mover/entregar) em silêncio, trocando o falso sucesso por um botão que não faz
 * nada — pior de diagnosticar. O aviso é emitido aqui, e não só devolvido no retorno, para que o
 * sinal exista mesmo se algum chamador ignorar a flag.
 */
export const buscarServicosPendentes = async (
  carId: string,
  opcoes?: { ignorarEsteiraIds?: string[] },
): Promise<ServicosPendentes> => {
  const ignorar = new Set(opcoes?.ignorarEsteiraIds ?? []);

  try {
    const atividades = (await getAtividadesCarro({ carId, isActive: true })) as AtividadeCarro[];
    const relevantes = (atividades || []).filter(
      (a) => !ignorar.has(a.esteiraId || '') && a.esteira?.sistema !== true,
    );

    return {
      inacabados: relevantes.filter((a) => isServicoEmAberto(a.estado)).map(nomeDaEsteira),
      naoRealizadas: relevantes.filter((a) => a.estado === 'PENDENTE').map(nomeDaEsteira),
      verificacaoIndisponivel: false,
    };
  } catch (err) {
    // Falha na consulta não bloqueia a ação do usuário (o aviso é informativo e o backend não
    // impede a Entrega com serviço em aberto) — mas também não pode se passar por "veículo sem
    // pendências". O toast usa toastId fixo porque a checagem se repete a cada tentativa.
    console.error('Erro ao verificar serviços pendentes do veículo:', err);
    notify.warning(
      'Não foi possível verificar se este veículo tem serviços em aberto. Confira antes de concluir.',
      { toastId: 'servicos-pendentes-indisponivel' },
    );
    return { inacabados: [], naoRealizadas: [], verificacaoIndisponivel: true };
  }
};

/**
 * Texto de confirmação para quando o veículo vai para a Entrega com serviço em aberto — ou
 * quando não foi possível verificar se há algum (at-60).
 *
 * Retorna null **somente** quando a verificação aconteceu e nada ficou pendente. Falha de
 * consulta produz mensagem própria: o silêncio afirmaria, por omissão, que o veículo está limpo.
 */
export const mensagemConfirmacaoEntrega = (pendentes: ServicosPendentes): string | null => {
  const partes: string[] = [];

  if (pendentes.verificacaoIndisponivel) {
    partes.push(
      'não foi possível verificar se este veículo tem serviços em aberto. Confira a situação do veículo antes de continuar.',
    );
  }
  if (pendentes.inacabados.length > 0) {
    partes.push(
      `Serviços que ficaram inacabados: ${pendentes.inacabados.join(', ')}. Eles não serão retomados.`,
    );
  }
  if (pendentes.naoRealizadas.length > 0) {
    partes.push(`Etapas planejadas que não foram realizadas: ${pendentes.naoRealizadas.join(', ')}.`);
  }

  if (partes.length === 0) return null;

  const conheceOsServicos = pendentes.inacabados.length > 0 || pendentes.naoRealizadas.length > 0;
  const fecho = conheceOsServicos
    ? 'Ao enviar o veículo para a Entrega o atendimento é encerrado e esses serviços ficam registrados como não concluídos.'
    : 'Ao enviar o veículo para a Entrega o atendimento é encerrado e qualquer serviço que ainda esteja em aberto fica registrado como não concluído.';

  return `Atenção: ${partes.join(' ')} ${fecho} Deseja continuar?`;
};
